import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { validIcon } from "../../common/proptypes";
import Feedback from "../../Feedback/Feedback";
import LiveRegion from "../LiveRegion/LiveRegion";
import { ANNOUNCEMENT_TIMEOUT, CLEAR_ANNOUNCEMENT_TIMEOUT, FEEDBACK_ANNOUNCEMENT_TIMEOUT } from "./constants";
import extractTheme from "./extractTheme";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const ERRORTEXT = "Error, ";
/**
 * PanelSelectionsDock is a React component that allows you to display
 * the feedback Instructions and selection of items count information to guide users through flows
 * and task completion. PanelSelectionsDock combines text, icons and visual
 * placement to provide contextual information.
 */

function PanelSelectionsDock(_ref) {
  let {
    className,
    feedbackIcon,
    feedbackMessage,
    feedbackRole,
    feedbackAriaLive,
    feedbackError,
    selectedCountMessage,
    feedbackId,
    selectedCountId,
    noFeedback
  } = _ref;
  const theme = useTheme();
  const {
    cssRoot,
    cssSelectedCount
  } = extractTheme({
    theme
  });
  const feedbackMessageContainerClass = clsx("pnr-panel-selections-dock__info-container", {
    "pnr-panel-selections-dock__info-container--alignment": noFeedback
  }, className);
  const feedbackMessageClass = clsx({
    "pnr-panel-selections-dock__feedback": selectedCountMessage
  });
  const feedbackLiveRegionKey = feedbackError ? `feeback-live-region-${selectedCountMessage}` : null;
  const feedbackErrorText = feedbackError && `${ERRORTEXT}${feedbackMessage}`;
  /**
   * Explanations for eslint-disable-line usage below.
   *
   * Important note about "eslint-disable jsx-a11y/aria-role":
   * Presently (10/9/2019) `role="text"` is not valid.
   * Without this disable command, a lint error is thrown;
   * however, the role is currently required in the A11Y
   * specs:
   *
   * NOTE: role="text" is officially not part of ARIA 1.1
   * and ignored by JAWS and NVDA, it is honored by VoiceOver
   * and helps with announcing content in one stop versus
   * splitting based on nested markup.
   */

  return _jsxs("div", {
    className: feedbackMessageContainerClass,
    css: cssRoot,
    children: [!noFeedback && _jsxs(React.Fragment, {
      children: [_jsx(Feedback, {
        size: "small",
        icon: feedbackIcon,
        theme: feedbackError ? "error" : "information",
        className: feedbackMessageClass,
        id: feedbackId
        /**
         * role="text" is only supported by VoiceOver right now,
         * and may be flagged by automated testing tools as being invalid.
         */
        // eslint-disable-next-line jsx-a11y/aria-role
        ,
        role: "text",
        children: _jsx("span", {
          children: feedbackMessage
        })
      }), _jsx(LiveRegion, {
        role: feedbackRole,
        politeness: feedbackAriaLive,
        "data-testid": "message-live-region",
        announcementDelay: FEEDBACK_ANNOUNCEMENT_TIMEOUT,
        clearAnnouncementDelay: CLEAR_ANNOUNCEMENT_TIMEOUT
        /**
         * A key (based on items selected)
         * is needed to trigger a re-render even when
         * the error message is the same.
         * E.g.
         * Scenario:
         * MinSelection is 2.
         * User aleady selected 2.
         * One record is unselected. Announcement should fire.
         * Another record is unselected. The announcement should fire again.
         */
        ,
        children: feedbackErrorText
      }, feedbackLiveRegionKey)]
    }), selectedCountMessage && _jsxs(React.Fragment, {
      children: [_jsx("span", {
        "data-testid": "selected-count",
        css: cssSelectedCount,
        id: selectedCountId,
        children: selectedCountMessage
      }), _jsx(LiveRegion, {
        role: "status",
        announcementDelay: ANNOUNCEMENT_TIMEOUT,
        clearAnnouncementDelay: CLEAR_ANNOUNCEMENT_TIMEOUT,
        children: selectedCountMessage
      })]
    })]
  });
}

PanelSelectionsDock.propTypes = process.env.NODE_ENV !== "production" ? {
  /** General message to be shown in Feedback component. */
  feedbackMessage: PropTypes.string.isRequired,

  /** The CSS class name of the root element. */
  className: PropTypes.string,

  /** Prop for notifying screen readers when content is updated. */
  feedbackAriaLive: PropTypes.string,

  /** Validity Status of Feedback. */
  feedbackError: PropTypes.bool,

  /** A React component of type Icon. See Iconography section. */
  feedbackIcon: validIcon,

  /** Id for the Feedback container. */
  feedbackId: PropTypes.string,

  /** The role of the Feedback message. */
  feedbackRole: PropTypes.string,

  /** Prop to hide the Feedback. Defaults to `false`.  */
  noFeedback: PropTypes.bool,

  /** Id for the Selected Count container. */
  selectedCountId: PropTypes.string,

  /** Prop for the selection of items count message. */
  selectedCountMessage: PropTypes.string
} : {};
PanelSelectionsDock.defaultProps = {
  className: null,
  feedbackIcon: null,
  selectedCountMessage: null,
  feedbackId: null,
  selectedCountId: null,
  feedbackRole: null,
  feedbackAriaLive: null,
  feedbackError: false,
  noFeedback: false
};
PanelSelectionsDock.displayName = "PanelSelectionsDock";
export default PanelSelectionsDock;